import { easypanelTargetSchema, githubTargetSchema, type PublishSettingsDto, type SiteDto } from '@ads/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { TextField } from '../components/Field';
import * as Ic from '../components/icons';
import { Seg } from '../components/Seg';
import { Actions } from '../components/Shell';
import { ApiError, api } from '../lib/api';
import { dateTime } from '../lib/format';
import { usePublishSettings, useSavePublishSettings, useSession, useSites } from '../lib/queries';

/* Configurações · publicação: onde os sites aprovados vão para o ar
   (EasyPanel) e onde ficam versionados (GitHub do cliente). */

type GhForm = { on: boolean; repo: string; branch: string; basePath: string; auth: 'token' | 'app'; secretRef: string; installationId: string };
type EpForm = { project: string; domainTemplate: string; mode: 'bundle' | 'github' };

const ghFrom = (s: PublishSettingsDto): GhForm => s.github
  ? { on: true, repo: s.github.repo, branch: s.github.branch, basePath: s.github.basePath, auth: s.github.auth, secretRef: s.github.secretRef ?? '', installationId: s.github.installationId ?? '' }
  : { on: false, repo: '', branch: 'main', basePath: 'sites', auth: s.server.githubApp ? 'app' : 'token', secretRef: '', installationId: '' };
const epFrom = (s: PublishSettingsDto): EpForm => s.easypanel
  ? { project: s.easypanel.project, domainTemplate: s.easypanel.domainTemplate, mode: s.easypanel.mode }
  : { project: s.server.defaultProject, domainTemplate: s.server.defaultDomain ?? '', mode: 'bundle' };

export function PublishConfig({ tabs }: { tabs: ReactNode }) {
  const { me } = useSession();
  const nav = useNavigate();
  const q = usePublishSettings();
  const sitesQ = useSites();
  const save = useSavePublishSettings();
  const owner = me.org.role === 'owner';
  const [gh, setGh] = useState<GhForm | null>(null);
  const [ep, setEp] = useState<EpForm | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string; details?: string[] } | null>(null);
  useEffect(() => { if (q.data && !gh) { setGh(ghFrom(q.data)); setEp(epFrom(q.data)); } }, [q.data, gh]);

  const s = q.data;
  const check = useMutation({
    mutationFn: () => api<{ github: { ok: boolean; message: string } | null; easypanel: { ok: boolean; message: string } | null }>(`/orgs/${me.org.id}/publish/check`, { method: 'POST' }),
  });

  const payload = () => {
    const github = gh?.on ? githubTargetSchema.safeParse({
      repo: gh.repo, branch: gh.branch || 'main', basePath: gh.basePath, auth: gh.auth,
      secretRef: gh.auth === 'token' ? gh.secretRef || null : null, installationId: gh.auth === 'app' ? gh.installationId || null : null,
    }) : null;
    const easypanel = s?.server.easypanel && ep?.domainTemplate ? easypanelTargetSchema.safeParse(ep) : null;
    const issues = [
      ...(github && !github.success ? github.error.issues.map(i => `GitHub · ${i.path.join('.')}: ${i.message}`) : []),
      ...(easypanel && !easypanel.success ? easypanel.error.issues.map(i => `EasyPanel · ${i.path.join('.')}: ${i.message}`) : []),
    ];
    return { issues, body: { github: github?.success ? github.data : null, easypanel: easypanel?.success ? easypanel.data : null } };
  };

  const onSave = () => {
    const p = payload();
    if (p.issues.length) { setMsg({ tone: 'err', text: 'Confira os campos antes de salvar.', details: p.issues }); return; }
    save.mutate(p.body, {
      onSuccess: () => { setMsg({ tone: 'ok', text: 'Configuração salva.' }); check.reset(); },
      onError: e => setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Falhou.', details: e instanceof ApiError && Array.isArray(e.details) ? (e.details as Array<{ path: string; message: string }>).map(d => `${d.path}: ${d.message}`) : undefined }),
    });
  };

  const example = ep?.domainTemplate.includes('{slug}') ? ep.domainTemplate.replace('{slug}', 'matriculas') : null;

  return (
    <section className="view on" data-v="config">
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Configurações</span><b>{me.org.name}</b></div>
          <h1>Publicação</h1>
          <p className="lede">Landing pages e sites aprovados no Studio vão para o ar no EasyPanel, com domínio e HTTPS, e cada publicação vira um commit no GitHub da {me.org.name}.</p>
        </div>
        <div className="tools">{tabs}</div>
      </div>

      <div className="grid2 config-grid">
        <div className="stack">
          <div className="card">
            <h3><Ic.Globe />Sites publicados</h3>
            <SiteList sites={sitesQ.data} loading={sitesQ.isLoading} onOpen={id => nav(`/pecas/${id}`)} onNew={() => nav('/studio/landing')} />
          </div>
          <div className="card">
            <h3><Ic.Rocket />Como uma página vai ao ar</h3>
            <ol className="flow">
              <li><b>Aprovação no canvas.</b> O que se vê no Studio é o HTML que será publicado — sem scripts, no design system do cliente.</li>
              <li><b>Pacote imutável.</b> Páginas, CSS, fontes e logo num .tar.gz com sha256 e registro de origem (peça, DNA, versão do design system).</li>
              <li><b>Commit no GitHub.</b> Uma pasta por site em <span className="mono">{gh?.basePath || 'sites'}/&lt;endereço&gt;/</span>, com Dockerfile e nginx prontos.</li>
              <li><b>Deploy no EasyPanel.</b> Um serviço por site, domínio com HTTPS automático, e o ADS confere se a versão no ar é a aprovada.</li>
            </ol>
          </div>
        </div>

        <div className="stack">
          {gh && (
            <div className="card">
              <div className="card-row">
                <h3><Ic.Branch />GitHub · versionamento</h3>
                <button type="button" className="pub-target mini" role="switch" aria-checked={gh.on} disabled={!owner} onClick={() => setGh({ ...gh, on: !gh.on })} aria-label="Versionar no GitHub">
                  <span className="pt-sw" aria-hidden="true"><i /></span>
                </button>
              </div>
              {gh.on ? (
                <div className="stack" style={{ gap: 16 }}>
                  <fieldset disabled={!owner} className="brief-set">
                    <TextField id="ghRepo" label="Repositório" value={gh.repo} placeholder="agencia/sites-cliente" spellCheck={false}
                      hint="Um repositório por cliente é o mais simples. Pode ser privado." onChange={v => setGh({ ...gh, repo: v.trim() })} />
                    <div className="grid c2">
                      <TextField id="ghBranch" label="Branch" value={gh.branch} spellCheck={false} onChange={v => setGh({ ...gh, branch: v.trim() })} />
                      <TextField id="ghPath" label="Pasta" value={gh.basePath} spellCheck={false} onChange={v => setGh({ ...gh, basePath: v.trim() })} />
                    </div>
                    <div className="f"><span className="lab" id="ghAuthL">Acesso</span>
                      <Seg labelledBy="ghAuthL" value={gh.auth} options={[{ value: 'token', label: 'Token' }, { value: 'app', label: 'GitHub App' }]} onChange={v => setGh({ ...gh, auth: v })} /></div>
                    {gh.auth === 'token' ? (
                      <TextField id="ghSecret" label="Variável com o token" value={gh.secretRef} placeholder="GITHUB_TOKEN" spellCheck={false}
                        hint={<>Token <b>fine-grained</b> com acesso só a este repositório e permissão <b>Contents: read and write</b>. O token fica no servidor; aqui vai só o nome da variável.</>}
                        onChange={v => setGh({ ...gh, secretRef: v.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} />
                    ) : (
                      <TextField id="ghInst" label="Número da instalação" value={gh.installationId} placeholder="12345678" spellCheck={false}
                        hint={s?.server.githubApp ? 'Aparece na URL da instalação da App no GitHub do cliente (…/installations/<número>).' : 'A GitHub App ainda não foi configurada no servidor (GITHUB_APP_ID e GITHUB_APP_PRIVATE_KEY).'}
                        invalid={!s?.server.githubApp}
                        onChange={v => setGh({ ...gh, installationId: v.replace(/\D/g, '') })} />
                    )}
                  </fieldset>
                  <Status t={s?.github ?? null} check={check.data?.github ?? null} />
                </div>
              ) : <p className="sub">Desligado. As publicações não serão versionadas — ligue para guardar cada versão no repositório do cliente.</p>}
            </div>
          )}

          {ep && s && (
            <div className="card">
              <h3><Ic.Cube />EasyPanel · hospedagem</h3>
              {s.server.easypanel ? (
                <div className="stack" style={{ gap: 16 }}>
                  <p className="sub">Painel <span className="mono">{s.server.easypanelUrl?.replace(/^https?:\/\//, '')}</span> — cada site vira um serviço neste projeto.</p>
                  <fieldset disabled={!owner} className="brief-set">
                    <div className="grid c2">
                      <TextField id="epProj" label="Projeto" value={ep.project} spellCheck={false} onChange={v => setEp({ ...ep, project: v.trim().toLowerCase() })} />
                      <div className="f"><span className="lab" id="epModeL">Origem do build</span>
                        <Seg labelledBy="epModeL" value={ep.mode} options={[{ value: 'bundle', label: 'Pacote do ADS' }, { value: 'github', label: 'GitHub' }]} onChange={v => setEp({ ...ep, mode: v })} /></div>
                    </div>
                    <TextField id="epDomain" label="Domínio dos sites" value={ep.domainTemplate} placeholder="{slug}.sites.suaagencia.com.br" spellCheck={false}
                      hint={example ? <>Ex.: <b>{example}</b>. Aponte um DNS curinga (*.sites…) para o servidor do EasyPanel.</> : 'Use {slug} onde entra o endereço de cada site.'}
                      onChange={v => setEp({ ...ep, domainTemplate: v.trim().toLowerCase() })} />
                    {ep.mode === 'github' && <p className="sub">No modo GitHub, o EasyPanel constrói a partir da pasta do site no repositório — o painel precisa ter acesso ao GitHub do cliente.</p>}
                  </fieldset>
                  <Status t={s.easypanel} check={check.data?.easypanel ?? null} />
                </div>
              ) : (
                <div className="stack" style={{ gap: 12 }}>
                  <p className="sub">Este servidor ainda não está ligado a um EasyPanel. Defina <span className="mono">EASYPANEL_URL</span>, <span className="mono">EASYPANEL_TOKEN</span> e <span className="mono">PUBLIC_URL</span> no ambiente do ADS.</p>
                  {s.server.adsHosting && <div className="pub-note"><Ic.Eye /><p><b>Enquanto isso:</b> os sites aprovados ficam no ar na hospedagem de prévia do próprio ADS, em <span className="mono">{(s.server.publicUrl ?? window.location.origin).replace(/^https?:\/\//, '')}/s/&lt;endereço&gt;/</span>.</p></div>}
                </div>
              )}
            </div>
          )}

          {msg && (
            <div className={`notice ${msg.tone === 'err' ? 'warn' : ''}`}>
              {msg.tone === 'err' ? <Ic.Warning /> : <Ic.Check />}
              <div><p>{msg.text}</p>{msg.details?.length ? <ul>{msg.details.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}</ul> : null}</div>
            </div>
          )}
        </div>
      </div>

      <Actions>
        <button className="btn-ghost" type="button" disabled={check.isPending || (!s?.github && !s?.easypanel)} onClick={() => check.mutate()}>
          {check.isPending ? 'Testando…' : 'Testar conexão'}
        </button>
        {owner && <button className="btn-primary" type="button" disabled={save.isPending || !gh} onClick={onSave}>{save.isPending ? 'Salvando…' : 'Salvar'}<Ic.Check /></button>}
      </Actions>
    </section>
  );
}

function Status({ t, check }: { t: { ready: boolean; problem: string | null } | null; check: { ok: boolean; message: string } | null }) {
  if (check) return <p className={`cfg-status ${check.ok ? 'ok' : 'bad'}`}>{check.ok ? <Ic.Check /> : <Ic.Warning />}{check.message}</p>;
  if (!t) return null;
  return t.ready
    ? <p className="cfg-status ok"><Ic.Check />Pronto. Use “Testar conexão” para confirmar com o serviço.</p>
    : <p className="cfg-status bad"><Ic.Warning />{t.problem}</p>;
}

function SiteList({ sites, loading, onOpen, onNew }: { sites: SiteDto[] | undefined; loading: boolean; onOpen: (artifactId: string) => void; onNew: () => void }) {
  if (loading) return <p className="sub">Carregando…</p>;
  if (!sites?.length) return (
    <div className="empty-sites">
      <p className="sub">Nenhum site publicado ainda. Gere uma landing page ou um website no Studio, revise no canvas e aprove.</p>
      <button className="btn-ghost" type="button" onClick={onNew}>Abrir Landing Page</button>
    </div>
  );
  return (
    <ul className="site-list">
      {sites.map(s => (
        <li key={s.id}>
          <div className="sl-main">
            <b>{s.title}</b>
            <span>
              <em data-s={s.publication?.status ?? 'none'}>{s.publication?.status === 'done' ? (s.url ? 'no ar' : 'versionado') : s.publication?.status === 'failed' ? 'falhou' : s.publication?.status === 'running' ? 'publicando' : '—'}</em>
              {s.slug}{s.publication ? ` · ${dateTime(s.publication.createdAt)}` : ''}
            </span>
          </div>
          <div className="sl-act">
            {s.url && <a href={s.url} target="_blank" rel="noopener" title={s.url}><Ic.External />Abrir</a>}
            {s.publication?.commitUrl && <a href={s.publication.commitUrl} target="_blank" rel="noopener"><Ic.Branch />Commit</a>}
            {s.artifactId && <button type="button" onClick={() => onOpen(s.artifactId!)}>Peça<Ic.ChevronRight /></button>}
          </div>
        </li>
      ))}
    </ul>
  );
}
